import React, { useEffect, useMemo } from 'react';
import { capitalize, has, get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { isLoading, selectAccounts, getRtiSettings } from '../../../../redux/slicers/accounts';
import { selectPermissions } from '../../../../redux/slicers/permissions';
import { accountsSlice } from '../../../../redux/slicers/accounts';
import { useAppSelector } from '../../../../redux/store';
import { Products, REBRANDED_PRODUCT_NAMES, NEW_PRODUCT_NAMES } from '../../../../services/api/consts';
import { useSplit } from '../../../../hooks';
import { SPLIT_CONSTANTS } from '../../../../services/split.io';

import Progress from '../../../widgets/progress';
import Alert from '../../../svgs/alert';
import useStyle from '../styles';
import { ACCOUNT_TYPE_OPTIONS } from '../account-form-screen/constant';
import { getInitialAccountType } from '../account-form-screen/utils';
import { AccountProperty } from './components/AccountProperty';
import { BrandsSection } from './components/BrandsSection';
import { MarketingSecurityToggle } from './components/MarketingSecurityToggle';

export const InfoTab = ({ accountId, account, networks }) => {
    const classes = useStyle();
    const dispatch = useDispatch();
    const permissions = useSelector(selectPermissions);
    const salesforceAccounts = useAppSelector(state => state.accounts.salesforceAccounts);
    const {
        regions = [],
        products = [],
        bundlePlans = []
    } = useSelector(selectAccounts);
    const license = useSelector(state => state.accounts.license[accountId]);
    const userCanWriteNetwork = account && permissions.network && permissions.network.write;
    const rtiSettings = useSelector(state => state.accounts.rtiSettings);
    const hasDefend = license?.products?.some(product => product.id === Products.OSS);
    const { isOn: isAgentIntentRebrand } = useSplit(SPLIT_CONSTANTS.PD_AGENT_INTENT_REBRAND);
    const productNames = isAgentIntentRebrand ? REBRANDED_PRODUCT_NAMES : NEW_PRODUCT_NAMES;
    const types = [];

    if (rtiSettings.isApiIntegration) {
        types.push('API Integration');
    }

    if (rtiSettings.isClientSideIntegration) {
        types.push('Client Side Integration');
    }

    const integrationTypes = types;

    useEffect(() => {
        if (
            hasDefend && (
                rtiSettings.networkId === undefined ||
                account.networkId !== rtiSettings.networkId
            )
        ) {
            dispatch(getRtiSettings(account.networkId));

            if (!has(rtiSettings, 'networkId') ||
                rtiSettings.networkId !== account.networkId
            ) {
                dispatch(accountsSlice.actions.resetRtiSettings(account.networkId));
            }
        }
    }, [account?.networkId, rtiSettings?.networkId]);

    const salesforceObject = useMemo(() => {
        const initialSalesforceAccount = account?.salesforceId || '';
        return salesforceAccounts.find(account => account.ID === initialSalesforceAccount);
    }, [account?.salesforceId, salesforceAccounts]);

    const getNameFromId = (options, id, formater = str => str) =>
        formater(options.reduce((found, current) => (current.id === id ? current.name : found), ''));

    const getSalesforceProperty = property => get(salesforceObject, property);

    const accountTypeValue = ACCOUNT_TYPE_OPTIONS.find(
        ({ value }) => getInitialAccountType(account?.accountType) === value
    )?.label;

    const whiteLabelDomain = get(networks, '[0].whiteLabelDomain', undefined);
    const servingDomain = get(whiteLabelDomain, 'domain', undefined);
    const location = get(networks, '[0].dataLocation', undefined);

    return (
        <div>
            <div className="account-details-container">
                <div className="account-details-section">
                    <p className="cq-heading">CONTACT INFO</p>
                    <AccountProperty property="Email" value={account?.email} />
                    <AccountProperty property="Domain" value={getSalesforceProperty('Domain')} />
                    <AccountProperty
                        property="Salesforce Account Name"
                        value={capitalize(getSalesforceProperty('Name'))}
                    />
                    <AccountProperty property="Salesforce Account ID" value={getSalesforceProperty('ID')} />
                </div>

                <div className="account-details-section">
                    <p className="cq-heading">CHEQ INFO</p>
                    <AccountProperty property="SE" value={getSalesforceProperty('SalesEngineer')} />
                    <AccountProperty property="CSM" value={getSalesforceProperty('CSM')} />
                    <AccountProperty property="AE" value={getSalesforceProperty('AE')} />
                    <AccountProperty
                        property="Region"
                        value={getNameFromId(regions, account?.regionId, str => str.toUpperCase())}
                    />

                    {account && !account.isAgency && (
                        <>
                            {dispatch(isLoading('getAssociatedNetworks')) || !whiteLabelDomain ? (
                                <div className="field-group">
                                    <Progress />
                                </div>
                            ) : (
                                <>
                                    <AccountProperty property="Data Location" value={location} />
                                    <AccountProperty property="Serving Domain" value={servingDomain} />
                                    {!!whiteLabelDomain?.tainted && (
                                        <div className={classes.attention}>
                                            <Alert style={{ marginRight: 5, marginTop: 5 }} />
                                            <div className="notice text-muted" style={{ fontSize: 14 }}>
                                                This domain is blocked by Ad-Blockers
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
                {license?.products !== undefined && (
                    <div className="account-details-section">
                        <p className="cq-heading">
                            <b>LICENSE</b> INFO
                        </p>

                        <AccountProperty property="Account Type" value={accountTypeValue} />
                        <AccountProperty
                            property="Products"
                            value={license?.products?.map((product, i) => (
                                <div key={i}>
                                    {productNames[product.id] || getNameFromId(
                                        products,
                                        product.id,
                                        str => str.charAt(0).toUpperCase() + str.substr(1)
                                    )}
                                </div>
                            ))}
                        />
                        <MarketingSecurityToggle networkId={networks?.[0]?.id ?? account?.networkId} disabled={!userCanWriteNetwork} />
                        {hasDefend && (
                            <div className="defend-integrations">
                                <div className="field-key">{isAgentIntentRebrand ? 'Agent Intent Integrations' : 'Cheq Defend Integrations'}</div>
                                {integrationTypes.length > 0 ? (
                                    <div className="integrations-item">
                                        {integrationTypes.map((type, index) => (
                                            <span key={index}>{type}</span>
                                        ))}
                                    </div>
                                ) : (
                                    <div>No Integrations Enabled</div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <BrandsSection
                account={account}
                networks={networks}
                userCanWriteNetwork={userCanWriteNetwork}
                bundlePlans={bundlePlans}
            />
        </div>
    );
};
